import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { useLanguage } from '../../../contexts/LanguageContext';
import Badge from '../../../components/ui/Badge';
import NotesButton from './NotesButton';
import { directiveNotesService } from '../../../services/notesService';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import { divisionLabel, divisionColor } from '../../../constants/divisions';
import { pendingDays, isPendingOverdue } from '../../../utils/directiveAlerts';

/**
 * Registre des directives.
 *
 * « Reçue le » affiche date_directive, la date portee par le document.
 * created_at (date de saisie) n'est ni affiche ici ni exporte : seule la
 * fiche le montre.
 */

// Variantes du Badge canonique : 'grey' | 'blue' | 'amber' | 'green' | 'red'.
export const OUTCOME_VARIANT = {
  pending: 'amber',
  extra: 'green',
  credit: 'red',
  no_impact: 'grey',
};

const formatDate = (value, language) => {
  if (!value) return '—';
  // date_directive est une date nue (YYYY-MM-DD). `new Date('2026-08-31')` la
  // lit en UTC puis l'affiche en heure locale : au Quebec, la veille. On la
  // fixe donc a midi local, hors de portee de tout decalage de fuseau.
  const d = new Date(`${String(value)?.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d?.getTime())) return '—';
  return d?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

// Tri par en-tete, meme comportement que le tableau Extras & Credits.
// Par defaut : date de reception (date_directive) du plus ancien au plus recent.
// Les valeurs vides restent en dernier dans les deux sens ; a egalite, par numero.
const DEFAULT_SORT = { key: 'date_directive', direction: 'asc' };
const OUTCOME_ORDER = ['pending', 'extra', 'credit', 'no_impact'];

const byNumber = (a, b) =>
  String(a?.number ?? '')?.localeCompare(String(b?.number ?? ''), undefined, { numeric: true });

const sortValue = (d, key, t) => {
  switch (key) {
    // date_directive est une date nue (YYYY-MM-DD) : la comparaison de chaines suffit.
    case 'date_directive': return d?.date_directive ? String(d?.date_directive)?.slice(0, 10) : '';
    case 'number': return d?.number ?? '';
    case 'nomenclature': return d?.nomenclature || '';
    case 'division': return d?.division ? divisionLabel(t, d?.division) : '';
    case 'title': return d?.title || '';
    case 'outcome': return OUTCOME_ORDER.indexOf(d?.outcome || 'pending');
    default: return '';
  }
};

const compareDirectives = (a, b, sortConfig, t) => {
  const key = sortConfig?.key;
  const va = sortValue(a, key, t);
  const vb = sortValue(b, key, t);
  if (va !== vb) {
    if (va === '') return 1;
    if (vb === '') return -1;
    const cmp = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va)?.localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    if (cmp !== 0) return sortConfig?.direction === 'desc' ? -cmp : cmp;
  }
  return byNumber(a, b);
};

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig?.key !== column) {
    return <Icon name="ChevronsUpDown" size={12} className="text-gray-400" />;
  }
  return sortConfig?.direction === 'asc'
    ? <Icon name="ChevronUp" size={12} className="text-primary" />
    : <Icon name="ChevronDown" size={12} className="text-primary" />;
};

// Le compte vient de la page : lu en base avec la liste (une directive absente
// = 0). En repli seulement : undefined = en cours, null = indisponible (« ? »).
// Le bouton reste cliquable dans tous les cas.
// Aucun document : icone grisee + infobulle « Aucun document joint » ; le
// bouton reste cliquable et ouvre les pieces comme d'habitude.
const DocumentsCell = ({ directive, count, onOpen }) => {
  const { t } = useTranslation();
  const empty = count === 0;
  const title = count === null
    ? t('extras.directives.table.documentsCountUnknown')
    : empty
      ? t('extras.directives.table.noDocuments')
      : t('extras.directives.table.documents');
  // Trombone orange + compteur : meme rendu que la colonne Actions des extras.
  const loading = count === undefined;
  const has = !loading && count > 0;
  return (
    <button
      type="button"
      onClick={() => onOpen?.(directive)}
      title={title}
      aria-busy={loading || undefined}
      className="relative p-1 rounded transition-colors group focus:outline-none focus:ring-2 focus:ring-primary/20"
    >
      <Icon
        name="Paperclip"
        size={13}
        className={`transition-colors ${has ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
      />
      <span
        className={`absolute -top-1 -right-1 inline-flex items-center justify-center text-[10px] rounded-full px-1 leading-tight font-medium ${
          has ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-400'
        }`}
        style={{ minWidth: '14px', minHeight: '13px', textAlign: 'center' }}
      >
        {loading ? <Icon name="Loader2" size={8} className="animate-spin text-gray-400" /> : count == null ? '?' : count}
      </span>
    </button>
  );
};

// Pastille de statut = LE controle d'issue d'une directive (un seul endroit) :
//   « A evaluer ▾ »            -> Creer un extra / Creer un credit / Sans impact
//                                 (actions immediates, comme avant)
//   « Extra cree ▾ » / « Credit cree ▾ » -> Revenir a « A evaluer » : selon le
//                                 statut de l'extra lie, suppression (brouillon,
//                                 soumis, rejete) ou blocage (approuve, lie a de
//                                 l'argent). Voir ExtrasPage / lot D9.
//   « Sans impact ▾ »          -> Revenir a « A evaluer » (confirmation courte)
// Le clic sur la pastille et dans son menu n'ouvre pas la fiche.
const OutcomePill = ({ directive, busy, onCreateExtra, onCreateCredit, onNoImpact, onRevertExtra, onChangeOutcome }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const outcome = directive?.outcome || 'pending';
  const isPending = outcome === 'pending';

  // Ferme le menu, puis lance l'action.
  const run = (action) => () => {
    setOpen(false);
    action?.(directive);
  };

  const itemClass =
    'flex items-center gap-2 w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap';

  return (
    <span className="inline-flex" onClick={(e) => e?.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('extras.directives.issue.menuLabel')}
        className="inline-flex rounded-full hover:brightness-95 transition focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
      >
        <Badge variant={OUTCOME_VARIANT?.[outcome] || 'grey'}>
          {busy ? <Icon name="Loader2" size={10} className="animate-spin" /> : null}
          {t(`extras.directives.outcome.${outcome}`)}
          <Icon name="ChevronDown" size={10} className="opacity-60" />
        </Badge>
      </button>
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} align="left" width={outcome === 'no_impact' ? 250 : 200}>
        <div role="menu">
          {isPending ? (
            <>
              <button type="button" role="menuitem" className={itemClass} onClick={run(onCreateExtra)}>
                <Icon name="Plus" size={13} className="text-green-600 shrink-0" />
                {t('extras.directives.actions.createExtra')}
              </button>
              <button type="button" role="menuitem" className={itemClass} onClick={run(onCreateCredit)}>
                <Icon name="Minus" size={13} className="text-red-500 shrink-0" />
                {t('extras.directives.actions.createCredit')}
              </button>
              <button type="button" role="menuitem" className={itemClass} onClick={run(onNoImpact)}>
                <Icon name="CircleSlash" size={13} className="text-gray-400 shrink-0" />
                {t('extras.directives.outcome.no_impact')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={run(outcome === 'no_impact' ? (d) => onChangeOutcome?.(d, 'pending') : onRevertExtra)}
              >
                <Icon name="Undo2" size={13} className="text-gray-400 shrink-0" />
                {t('extras.directives.issue.backToPending')}
              </button>
              {/* Aide : seulement pour « Sans impact » (un extra / credit se delie). */}
              {outcome === 'no_impact' && (
                <p className="px-3 pb-2 pt-0.5 text-[10px] leading-snug text-gray-400 whitespace-normal">
                  {t('extras.directives.issue.backHint')}
                </p>
              )}
            </>
          )}
        </div>
      </PortalDropdown>
    </span>
  );
};

// Menu ⋮ d'une directive, toujours present, quelle que soit l'issue.
// L'issue ne s'y change plus : c'est la pastille de statut (OutcomePill).
const DirectiveActionsMenu = ({ directive, disabled, canDelete, onEdit, onHistory, onDelete, onLinkExtra, onUnlinkExtra }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const items = [
    { label: t('extras.directives.actions.edit'), icon: 'Pencil', onClick: () => onEdit?.(directive) },
    // Detacher si un extra est lie ; Relier seulement « A evaluer » ; rien sur « Sans impact ».
    ...(directive?.extra_id
      ? [{ label: t('extras.directives.link.menuUnlink'), icon: 'Unlink', onClick: () => onUnlinkExtra?.(directive) }]
      : (directive?.outcome || 'pending') === 'pending'
        ? [{ label: t('extras.directives.link.menuLink'), icon: 'Link', onClick: () => onLinkExtra?.(directive) }]
        : []),
    { label: t('extras.directives.actions.history'), icon: 'History', onClick: () => onHistory?.(directive) },
    ...(canDelete
      ? [{ label: t('extras.directives.actions.delete'), icon: 'Trash2', variant: 'danger', onClick: () => onDelete?.(directive) }]
      : []),
  ];

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={t('extras.directives.actions.menu')}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} items={items} width={220} />
    </>
  );
};

const DirectivesTable = ({
  directives,
  onCreateExtra,
  onCreateCredit,
  onNoImpact,
  onOpenDocuments,
  busyId,
  documentCounts = {},
  countsComplete = false,
  highlightId,
  canDelete = false,
  onEdit,
  onChangeOutcome,
  onHistory,
  onDelete,
  onOpenDetail,
  onLinkExtra,
  onUnlinkExtra,
  onRevertExtra,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const { id: projectId } = useParams();

  // Notes internes : compteurs + derniere note de toutes les directives du
  // projet, en une requete ; relue apres chaque ajout / modification / suppression.
  const [notesSummary, setNotesSummary] = useState({});
  const [notesVersion, setNotesVersion] = useState(0);
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    directiveNotesService.listSummaryByProject(projectId)
      ?.then((summary) => { if (alive) setNotesSummary(summary || {}); })
      ?.catch((err) => console.error('[notes:directive] resume impossible:', err));
    return () => { alive = false; };
  }, [projectId, notesVersion]);

  const onSort = (key) => {
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  if (!directives || directives?.length === 0) {
    return (
      <div className="p-12 text-center">
        <Icon name="ClipboardList" size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-500">{t('extras.directives.empty.none')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('extras.directives.empty.createFirst')}</p>
      </div>
    );
  }

  const rows = [...directives]?.sort((a, b) => compareDirectives(a, b, sortConfig, t));

  const SortableHeader = ({ column, label, className = '' }) => (
    <th
      className={`text-left text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100 ${className}`}
      style={{ whiteSpace: 'nowrap' }}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <SortIcon column={column} sortConfig={sortConfig} />
      </div>
    </th>
  );

  return (
    <div>
      {/* Largeurs fixes (table-fixed) : seule « Titre & description » est souple
          et se tronque. Division masquee sous 1 280 px (deja dans la fiche). */}
      <table className="w-full table-fixed">
        <colgroup>
          <col style={{ width: '104px' }} />
          <col style={{ width: '60px' }} />
          <col style={{ width: '150px' }} />
          <col className="hidden xl:table-column" style={{ width: '120px' }} />
          <col />
          <col style={{ width: '200px' }} />
          <col style={{ width: '108px' }} />
        </colgroup>
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <SortableHeader column="date_directive" label={t('extras.directives.table.receivedOn')} />
            <SortableHeader column="number" label={t('extras.directives.table.number')} />
            <SortableHeader column="nomenclature" label={t('extras.directives.table.nomenclature')} />
            <SortableHeader column="division" label={t('extras.directives.table.division')} className="hidden xl:table-cell" />
            <SortableHeader column="title" label={t('extras.directives.table.title')} />
            <SortableHeader column="outcome" label={t('extras.directives.table.status')} />
            <th className="text-right text-xs font-medium text-gray-500 px-4 py-3 bg-gray-50" style={{ whiteSpace: 'nowrap' }}>{t('extras.directives.table.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows?.map((d) => {
            const busy = busyId === d?.id;

            return (
              <tr
                key={d?.id}
                id={`directive-row-${d?.id}`}
                // Clic sur la ligne : ouvre la fiche. Pieces et Actions arretent le clic.
                onClick={() => onOpenDetail?.(d)}
                className={`transition-colors cursor-pointer ${
                  highlightId && d?.id === highlightId
                    ? 'bg-orange-50 ring-2 ring-inset ring-primary/40'
                    : 'hover:bg-gray-50/60'
                }`}
              >
                {/* Reçue le — date_directive, jamais created_at */}
                <td className="px-4 py-3 align-middle text-xs text-gray-600" style={{ whiteSpace: 'nowrap' }}>
                  {formatDate(d?.date_directive, language)}
                </td>

                {/* N° */}
                <td className="px-4 py-3 align-middle" style={{ whiteSpace: 'nowrap' }}>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-100 text-orange-600">
                    {d?.number ?? '—'}
                  </span>
                </td>

                {/* Nomenclature + professionnel dessous */}
                <td className="px-4 py-3 align-middle">
                  <button
                    type="button"
                    onClick={(e) => { e?.stopPropagation(); onOpenDetail?.(d); }}
                    title={t('extras.directives.detail.open')}
                    className="block max-w-full text-left text-xs font-medium text-gray-900 leading-tight truncate hover:text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                  >
                    {d?.nomenclature || '—'}
                  </button>
                  <p className="text-[10px] text-gray-400 leading-tight truncate">
                    {d?.professional?.company_name || '—'}
                  </p>
                </td>

                {/* Division — masquee sous 1 280 px */}
                <td className="hidden xl:table-cell px-4 py-3 align-middle overflow-hidden" style={{ whiteSpace: 'nowrap' }}>
                  {d?.division ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${divisionColor(d?.division)?.bg} ${divisionColor(d?.division)?.text}`}>
                      {divisionLabel(t, d?.division)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>

                {/* Titre & description */}
                <td className="px-4 py-3 align-middle min-w-0">
                  <p className="text-xs text-gray-800 leading-tight truncate">{d?.title || '—'}</p>
                  {d?.description && (
                    <p className="text-[10px] text-gray-400 leading-tight truncate">{d?.description}</p>
                  )}
                </td>

                {/* Statut */}
                <td className="px-4 py-3 align-middle overflow-hidden" style={{ whiteSpace: 'nowrap' }}>
                  <div className="flex items-center gap-1.5">
                    {/* Pastille = controle d'issue (menu) */}
                    <OutcomePill
                      directive={d}
                      busy={busy}
                      onCreateExtra={onCreateExtra}
                      onCreateCredit={onCreateCredit}
                      onNoImpact={onNoImpact}
                      onRevertExtra={onRevertExtra}
                      onChangeOutcome={onChangeOutcome}
                    />
                    {/* « A evaluer » : jours depuis la reception, toujours visibles.
                        Gris sous le seuil, ambre avec alerte a partir du seuil. */}
                    {pendingDays(d) != null && (() => {
                      const days = Math.max(0, pendingDays(d));
                      const overdue = isPendingOverdue(d);
                      return (
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                            overdue ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'
                          }`}
                          title={t('extras.directives.table.overdue', { days })}
                        >
                          {overdue && <Icon name="AlertTriangle" size={10} />}
                          {t('extras.directives.table.overdueShort', { days })}
                        </span>
                      );
                    })()}
                  </div>
                </td>

                {/* Actions — pieces · notes · menu ⋮, comme les extras. Rien ici
                    n'ouvre la fiche (l'issue se change par la pastille de statut). */}
                <td
                  className="px-4 py-3 align-middle text-right cursor-default"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={(e) => e?.stopPropagation()}
                >
                  <div className="inline-flex items-center gap-2">
                  <DocumentsCell
                    directive={d}
                    count={countsComplete ? (documentCounts?.[d?.id] ?? 0) : documentCounts?.[d?.id]}
                    onOpen={onOpenDocuments}
                  />
                  <NotesButton
                    entityType="directive"
                    entityId={d?.id}
                    projectId={projectId || d?.project_id}
                    subtitle={`${d?.nomenclature || '—'} · ${d?.title || '—'}`}
                    summary={notesSummary?.[d?.id]}
                    onChanged={() => setNotesVersion((v) => v + 1)}
                  />
                  <DirectiveActionsMenu
                    directive={d}
                    disabled={busy}
                    canDelete={canDelete}
                    onEdit={onEdit}
                    onHistory={onHistory}
                    onDelete={onDelete}
                    onLinkExtra={onLinkExtra}
                    onUnlinkExtra={onUnlinkExtra}
                  />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DirectivesTable;
